<template>
  <div class="tab-pane-section">
    <div class="tab-pane-header">
      <div class="tab-pane-header__title"> Reports overview </div>
      <div class="tab-pane-header__filter">
        <el-date-picker
            @change="handleDatePickerChange"
            v-model="dateRange"
            :picker-options="datePickerOptions"
            type="daterange"
            class="date-range-picker"
            format="MM-dd-yyyy" :clearable="false"
            value-format="yyyy-MM-dd"
            size="mini"  align="right"
            start-placeholder="Start Date"
            end-placeholder="End Date">
        </el-date-picker>

        <el-button-group>
          <el-button
              size="mini" v-for="item in suitableFilters"
              v-bind:key="item.filter"
              @click="handleDateRangeFilterChange(item.filter)"
              :class="{'active-filter-btn': dateRangeFilter === item.filter }"
          >
            {{ item.title }}
          </el-button>
        </el-button-group>
      </div>
    </div>
    <div class="tab-pane-body">
      <div class="charts-row">
        <div class="chart-block" v-for="chart in chartReportsData" v-bind:key="chart.title">
          <div class="chart-block-header">
            <div class="chart-block-header__title">
              {{ chart.title }}
            </div>
            <div class="chart-block-header__value">
              <span v-if="chart.title !== 'New customers'" class="value-prefix"> $ </span>
              <span class="value-text"> {{ chart.title !== 'New customers' ? chart.value.toFixed(2) : chart.value }} </span>
            </div>
          </div>

          <div class="chart-block-body">
            <apexcharts
                width="97%"
                height="300px"
                :options="chart.options"
                :series="chart.series"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import VueApexCharts from 'vue-apexcharts'
  import analyticFormatData from '@/mixins/analyticFormatData'

  export default {
    name: 'PaymentChartsReports',
    mixins: [analyticFormatData],
    components: {
      apexcharts: VueApexCharts
    },
    data() {
      return {
        dateRange: [],
        dateRangeFilter: 'currentMonth',
        suitableFilters: [
          { filter: 'currentWeek', title: 'Week' },
          { filter: 'currentMonth', title: 'Month' },
          { filter: 'currentYear', title: 'Year' }
        ],
        datePickerOptions: {
          firstDayOfWeek: 1,
          disabledDate: this.handleMinMaxDatePicker
        }
      }
    },
    computed: {
      ...mapGetters({
        newCustomers: 'analytic/getNewCustomers',
        successPayments: 'analytic/getSuccessfulPayments'
      }),
      chartReportsData() {
        const formattedCharts = [];
        const yearly = this.dateRangeFilter === 'currentYear' || this.dateRange[0].split('-').shift() < new Date().getFullYear()

        const sourceCharts = [
          { title: 'New customers', data: yearly ? this.formatDataByMonths('count', this.newCustomers) : this.newCustomers },
          { title: 'Success payments', data: yearly ? this.formatDataByMonths('sum', this.successPayments) : this.successPayments }
        ]

        sourceCharts.forEach(chart => {
          const chartOpts = {
            series: [
              { name: '', data: [] }
            ],
            options: {
              chart: {
                type: 'line',
                animations: { speed: 200 },
                toolbar: { show: false }
              },
              noData: {
                text: 'No data for current period'
              },
              colors: ['#3366cd','#4a596ade'],
              stroke: { curve: 'straight', width: 1 },
              dataLabels: { enabled: true },
              grid: { show: false },
              legend: { show: false },
              xaxis: {
                categories: [],
                labels: { show: true },
                tooltip: { enabled: false }
              },
              yaxis: {
                labels: { show: true },
                tooltip: { enabled: false }
              },
              tooltip: {
                y: {
                  formatter: value => chart.title !== 'New customers' ? `$ ${ Number(value) }`: Number(value)
                }
              }
            }
          }

            chartOpts.series[0].name = chart.title

          if (!chart.data) {
            chartOpts.options.xaxis.categories = []
            chartOpts.series[0].data = []
          } else {
            chart.data.forEach(item => {
              let keys = Object.keys(item)

              if (chart.data.length === 1) {
                chartOpts.options.xaxis.categories
                    .push(
                        this.dateRangeFilter === 'currentYear' ? this.date(this.dateRange[0]).getMonthName : this.dateRange[0],
                        this.dateRangeFilter === 'currentYear' ? item[keys[0]] : item[keys[0]].split('-').pop()
                    )
                chartOpts.series[0].data.push(0, item[keys[1]])
              } else {
                chartOpts.options.xaxis.categories.push(this.dateRangeFilter === 'currentYear' ? item[keys[0]] : item[keys[0]].split('-').pop())
                chartOpts.series[0].data.push(item[keys[1]])
              }
            })
          }

          formattedCharts.push({
            title: chart.title,
            options: chartOpts.options,
            series: chartOpts.series,
            value: chartOpts.series[0].data.reduce((acc, next) => Number(acc) + Number(next), 0)
          })
        })

        return formattedCharts
      }
    },
    methods: {
      requestReportsData() {
        this.$store.dispatch('analytic/getNewCustomers', this.dateRange)
        this.$store.dispatch('analytic/getSuccessPayments', this.dateRange)
      },
      handleDatePickerChange(range) {
        this.dateRange = range
        this.requestReportsData()
      },
      handleDateRangeFilterChange(filter) {
        this.dateRangeFilter = filter
        this.dateRange = [this.date()[filter], this.date().today]

        this.requestReportsData()
      },
      formatDataByMonths(field, data) {
        let result = []

        if (data.length > 1 && data[0].date.split('-').length > 2) {
          const uniqueMonths = [...new Set(data.map(item => this.date(item.date).getMonthName))]

          uniqueMonths.forEach(month => {
            const monthlyCounts = []
            data.filter(item => this.date(item.date).getMonthName === month).forEach(item => monthlyCounts.push(Number(item[field])))

            result.push({ date: month, count: field === 'count' ? monthlyCounts.reduce((acc, curr) => acc + curr) : monthlyCounts.reduce((acc, curr) => acc + curr).toFixed(0)})
          })
        } else {
          data.forEach(item => result.push({ date: this.date(item.date).getMonthName, count: field === 'count' ? item[field] : Number(item[field]).toFixed(0) }))
        }

        return result
      }
    },
    created() {
      this.dateRange = [this.date()[this.dateRangeFilter], this.date().today]
      this.requestReportsData()
    }
  }
</script>

<style lang="scss" scoped>
  .tab-pane-section {
    padding-bottom: 25px;

    .tab-pane-header {
      border-bottom: 1px solid #E4E7ED;

      &__title {
        padding-bottom: 10px;
        font-size: 28px;
        font-weight: 600;

        @media (max-width: 550px) {
          text-align: center;
        }
      }

      &__filter {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        padding-bottom: 10px;

        @media (max-width: 550px) {
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }

        .date-range-picker {
          @media (max-width: 550px) {
            width: 75%;
            margin-bottom: 5px;
          }
          @media (min-width: 550px) {
            width: 35%;
          }
          @media (min-width: 768px) {
            width: 45%;
          }
          @media (min-width: 992px) {
            width: 35%;
          }
          @media (min-width: 1100px) {
            width: 25%;
          }
          @media (min-width: 1200px) {
            width: 15%;
          }
        }
      }
    }

    .tab-pane-body {
      padding-top: 10px;

      .charts-row {
        display: flex;
        flex-direction: row;
        justify-content: space-between;

        @media (max-width: 992px) {
          flex-direction: column;
        }

        .chart-block {
          width: 49%;
          padding: 5px;
          border: 1px solid #E4E7ED;
          border-radius: 5px;

          @media (max-width: 992px) {
            width: 97%;
            margin: 5px 0 5px 0;
          }

          &-header {
            &__title {
              padding: 10px 0 0 20px;
              font-weight: 600;
              color: #4a596ade;
            }

            &__value {
              padding-left: 20px;
              color: #3366cd;
              font-size: 21px;
              font-weight: 400;
            }
          }

          &-body {
            position: relative;
          }
        }
      }
    }
  }
  .active-filter-btn {
    font-weight: 700;
    color: #3366cd;
    background: #3366cd4f;
  }
</style>
